import React, { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import Dropdown from 'react-bootstrap/Dropdown';
import Checkbox from '@material-ui/core/Checkbox';

import useDocuments from './components/hooks/useDocuments';
import SubText from '../../shared/components/SubText/SubText';
import iconSet from '../../shared/images/teambuildr-selection.json';
import useWindowSize from '../../shared/hooks/useWindowSize';
import Table from '../../shared/components/Table/Table';
import Spinner from '../../shared/components/Spinner/Spinner';
import Tooltip from '../../shared/_TBComponents/Tooltip';

import {
  setCurrentRowRedux,
  setActiveReduxModal,
  setModalShareLinkRedux,
  setSelectedRowsRedux,
  setSingleActionRedux,
} from './ducks/documentsActions';

const conditionalRowStyles = [
  {
    when: (row) => row,
    style: () => ({
      paddingLeft: '10px',
    }),
  },
];

const SpinnerWrapper = styled('div')`
  display: flex;
  margin-top: 200px;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const customNoRecordComponent = () => {
  const TextBox = styled('div')`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    margin-top: 10px;
    font-size: 20px;
  `;
  const TextAndIcon = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    margin-top: 100px;
  `;
  return (
    <TextAndIcon>
      <IcomoonReact iconSet={iconSet} size={100} icon='file' />
      <TextBox>No documents/links are available</TextBox>
    </TextAndIcon>
  );
};

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      href=''
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      <IcomoonReact
        iconSet={iconSet}
        size={25}
        icon='dots'
        color={isHovered ? '#10CD8C' : '#333333'}
      />
    </Link>
  );
});

const customTableStyle = {
  paddingBottom: '130px',
};

// Access cell renderer moved outside component to avoid re-creation on every render
const AccessCell = ({ row }) => {
  const filteredGroups = row.userGroupAccess.filter((group) => group.name !== null);
  const filteredIndividuals = row.individualAccess.filter(
    (ind) => ind.first !== null && ind.last !== null,
  );
  const hasGroups = filteredGroups.length > 0;
  const hasIndividuals = filteredIndividuals.length > 0;

  if (row.globalAccess) {
    return <span style={{ fontSize: '12px' }}>Everyone</span>;
  }

  // Column display text
  let displayText = '';
  if (hasGroups) {
    displayText += filteredGroups.length > 2
      ? `${filteredGroups.length} groups`
      : filteredGroups.map((group) => group.name).join(', ');
  }
  if (hasIndividuals) {
    if (displayText) displayText += ', ';
    displayText += filteredIndividuals.length === 1
      ? `${filteredIndividuals[0].first} ${filteredIndividuals[0].last}`
      : `${filteredIndividuals.length} individuals`;
  }

  // Tooltip header content
  const parts = [];
  if (hasGroups) {
    parts.push(`Groups:\n${filteredGroups.map((group) => group.name).join('\n')}`);
  }
  if (hasIndividuals) {
    parts.push(`Individuals:\n${filteredIndividuals.map((ind) => `${ind.first} ${ind.last}`).join('\n')}`);
  }

  const tooltipText = parts.join('\n\n');

  return (
    <Tooltip
      id={`access-tooltip-${row.id}`}
      displayText={displayText}
      hoverText={tooltipText}
      place='top'
      displayColor='#444444'
      displaySize='12px'
      cursor='default'
      tooltipStyles={{
        whiteSpace: 'pre-line',
        fontSize: '14px',
        zIndex: 2,
      }}
    />
  );
};

AccessCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number,
    globalAccess: PropTypes.bool,
    userGroupAccess: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
    })),
    individualAccess: PropTypes.arrayOf(PropTypes.shape({
      first: PropTypes.string,
      last: PropTypes.string,
    })),
  }).isRequired,
};

// Cell renderer function to avoid creating new component on every render
const renderAccessCell = (row) => <AccessCell row={row} />;

// Actions cell renderer function
const renderActionsCell = ({
  row,
  isAdmin,
  onRowClick,
  onSetCurrentRow,
  onSetActiveModal,
  onSetModalShareLink,
  onDropdownDeleteClick,
}) => (
  <ActionsCell
    row={row}
    isAdmin={isAdmin}
    onRowClick={onRowClick}
    onSetCurrentRow={onSetCurrentRow}
    onSetActiveModal={onSetActiveModal}
    onSetModalShareLink={onSetModalShareLink}
    onDropdownDeleteClick={onDropdownDeleteClick}
  />
);

// Actions cell component - moved outside to avoid re-creation on every render
const ActionsCell = ({
  row,
  isAdmin,
  onRowClick,
  onSetCurrentRow,
  onSetActiveModal,
  onSetModalShareLink,
  onDropdownDeleteClick,
}) => {
  if (!isAdmin) {
    return (
      <dotContainer onClick={() => onRowClick(row)}>
        <IcomoonReact iconSet={iconSet} size={25} icon='right-arrow' />
      </dotContainer>
    );
  }
  if (row.id) {
    return (
      <DropDownMenuWrapper>
        <Dropdown bsPrefix='trick-fix'>
          <Dropdown.Toggle as={CustomToggle} />
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => {
                onSetCurrentRow(row);
                onSetActiveModal('update_doc_modal');
              }}
            >
              <IcomoonReact iconSet={iconSet} size={12} icon='pencil' />
              <SubText className='drop-icon-text' fontSize={14}>
                Update
              </SubText>
            </Dropdown.Item>
            <Dropdown.Divider />
            {row.type === 1 || row.type === 2 ? (
              <Dropdown.Item
                onClick={() => {
                  onSetModalShareLink(
                    row.type === 1 ? `http://doc.tbldr.co/${row.id}` : row.url,
                  );
                  onSetActiveModal('share_modal');
                }}
              >
                <IcomoonReact iconSet={iconSet} size={12} icon='share' />
                <SubText className='drop-icon-text' fontSize={14}>
                  Share
                </SubText>
              </Dropdown.Item>
            ) : null}
            {row.type === 1 ? <Dropdown.Divider /> : null}
            <Dropdown.Item onClick={() => onDropdownDeleteClick(row)}>
              <IcomoonReact iconSet={iconSet} size={12} icon='trashcan' />
              <SubText className='drop-icon-text' fontSize={14}>
                Delete
              </SubText>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </DropDownMenuWrapper>
    );
  }
  return null;
};

ActionsCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.number,
    url: PropTypes.string,
  }).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onSetCurrentRow: PropTypes.func.isRequired,
  onSetActiveModal: PropTypes.func.isRequired,
  onSetModalShareLink: PropTypes.func.isRequired,
  onDropdownDeleteClick: PropTypes.func.isRequired,
};

const DropDownMenuWrapper = styled('div')`
  width: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 20%;
  color: #333333;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  .dropdown-item {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 15px;
    span {
      color: #999999;
    }
    .drop-icon-text {
      margin-left: 10px;
      padding-top: 2px;
    }
    :hover {
      transition: linear 0s;
      background: #006a65;
      color: white;
      span {
        color: white;
      }
      svg {
        fill: white;
      }
    }
  }
  .dropdown-menu {
    padding: 0px;
  }
  .dropdown-divider {
    margin: 0px;
  }
`;

const CustomTable = ({
  data,
  setCurrentFolder,
  setActiveSearchString,
  currentFolder,
}) => {
  const { handleFetchFile } = useDocuments();
  const windowWidth = useWindowSize().width;

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isDocumentsLoading = useSelector(
    (state) => state.documents.ui.isDocumentsLoading,
  );
  const isFolderLoading = useSelector(
    (state) => state.documents.ui.isFolderLoading,
  );
  const clearRowsToggle = useSelector(
    (state) => state.documents.data.clearRowsToggle,
  );

  const dispatch = useDispatch();

  const setCurrentRow = (row) => {
    dispatch(setCurrentRowRedux(row));
  };

  const setActiveModal = (activeModalString) => {
    dispatch(setActiveReduxModal(activeModalString));
  };

  const setModalShareLink = (activeModalString) => {
    dispatch(setModalShareLinkRedux(activeModalString));
  };

  const setSelectedRows = (newSelectedRows) => {
    dispatch(setSelectedRowsRedux(newSelectedRows));
  };

  const setSingleAction = (bool) => {
    dispatch(setSingleActionRedux(bool));
  };

  const fileFetcherDownloader = async (accountCode, docId) => {
    await handleFetchFile({ accountCode, docId });
  };

  const handleDropdownDeleteClick = (row) => {
    const selectedRow = { id: row.id, type: row.type };
    setSelectedRows([selectedRow]);
    setSingleAction(true);
    setActiveModal('delete_docs_modal');
  };

  const handleSelectedRowChange = (rows) => {
    const validRowsArray = rows?.selectedRows && Array.isArray(rows.selectedRows)
      ? rows.selectedRows
      : [];
    const newArray = validRowsArray
      .map((row) => ({
        id: row.id,
        type: row.type,
      }))
      .filter((row) => row.id != null && row.id !== undefined && Number.isInteger(row.id));
    setSelectedRows(newArray);
  };

  const handleRowClicked = (row) => {
    if (row.type === 0) {
      setSelectedRows([]);
      setCurrentFolder(row);
      setActiveSearchString('');
    }
    if (row.type === 1) {
      const { accountCode } = currentUser;
      const docId = row.id;
      fileFetcherDownloader(accountCode, docId, row.name);
    }
    if (row.type === 2) {
      window.open(row.url, '_blank');
    }
  };

  const columns = [
    {
      name: 'Type',
      selector: 'type',
      sortable: true,
      cell: (row) => {
        let returnOb;
        if (row.type === 1) {
          returnOb = <IcomoonReact iconSet={iconSet} size={25} icon='file' />;
        } else if (row.type === 0) {
          returnOb = <IcomoonReact iconSet={iconSet} size={25} icon='folder' />;
        } else if (row.type === 2) {
          returnOb = <IcomoonReact iconSet={iconSet} size={25} icon='globe' />;
        }
        return returnOb;
      },
      width: '65px',
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
      sortFunction: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      style: {
        'font-weight': 'bold',
      },
      minWidth: '200px',
      maxWidth: !currentUser.admin ? '350px' : '300px',
      grow: 1,
    },
    {
      name: 'Description',
      selector: (row) => (row.description ? row.description : '-'),
      center: true,
      omit: windowWidth <= 768,
      grow: 1,
      maxWidth: !currentUser.admin ? '500px' : '300px',
    },
    {
      name: 'Access',
      selector: (row) => row.id,
      cell: renderAccessCell,
      sortable: true,
      sortFunction: (a, b) => {
        // Global access comes first because it is the most broadly accessible
        if (a.globalAccess && !b.globalAccess) return -1;
        if (!a.globalAccess && b.globalAccess) return 1;
        if (a.globalAccess && b.globalAccess) return 0;

        // Count groups and individuals
        const aGroups = a.userGroupAccess.filter((group) => group.name !== null).length;
        const aIndividuals = a.individualAccess.filter(
          (ind) => ind.first !== null && ind.last !== null,
        ).length;
        const bGroups = b.userGroupAccess.filter((group) => group.name !== null).length;
        const bIndividuals = b.individualAccess.filter(
          (ind) => ind.first !== null && ind.last !== null,
        ).length;

        // Sort by groups first then by individuals
        if (bGroups !== aGroups) {
          return bGroups - aGroups;
        }

        return bIndividuals - aIndividuals;
      },
      omit: !currentUser.admin || windowWidth <= 768,
      center: true,
      grow: 1,
      maxWidth: '300px',
    },
    {
      name: 'Added',
      selector: 'dateAdded',
      sortable: true,
      sortFunction: (a, b) => Date.parse(a.dateAdded) - Date.parse(b.dateAdded),
      omit: windowWidth <= 992,
      grow: 1,
      center: true,
      width: '200px',
    },
    {
      cell: (row) => renderActionsCell({
        row,
        isAdmin: currentUser.admin,
        onRowClick: handleRowClicked,
        onSetCurrentRow: setCurrentRow,
        onSetActiveModal: setActiveModal,
        onSetModalShareLink: setModalShareLink,
        onDropdownDeleteClick: handleDropdownDeleteClick,
      }),
      right: true,
      width: !currentUser.admin ? '40px' : '60px',
      style: {
        /**
         * Added margin here to make the header columns centered
         * for the rest of the table. There doesn't seem to be a
         * way to customize an individual headCell style, so was able to
         * work around it by adding margin which isn't carried over
         * to the headCell like the width does above.
         */
        marginLeft: '10px',
        marginRight: '10px',
      },
    },
  ];

  return (
    <Table
      highlightOnHover
      pointerOnHover
      noHeader
      fitContainer
      data={data}
      selectableRows={!!currentUser.admin}
      columns={columns}
      noDataComponent={customNoRecordComponent()}
      progressComponent={(
        <SpinnerWrapper>
          <Spinner large />
        </SpinnerWrapper>
      )}
      progressPending={
        isDocumentsLoading || (isFolderLoading && currentFolder !== undefined)
      }
      style={customTableStyle}
      conditionalRowStyles={conditionalRowStyles}
      onSelectedRowsChange={(rows) => handleSelectedRowChange(rows)}
      onRowClicked={(row) => handleRowClicked(row)}
      clearSelectedRows={clearRowsToggle}
      selectableRowsComponent={Checkbox}
      selectableRowsComponentProps={{ color: 'primary', size: 'small' }}
    />
  );
};

CustomTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setCurrentFolder: PropTypes.func.isRequired,
  setActiveSearchString: PropTypes.func.isRequired,
  currentFolder: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]).isRequired,
};

export default CustomTable;
